import { gqlFetch } from '@/service/gql'
import { gql } from '@amzn/amp-playback'
import { type SanitizedResult } from '@amzn/amp-gql'
import { ctx } from '@/util/context'
import { getAuth } from '@/auth/authRef'

const { errorHandler } = ctx('podcastEpisodeProgressService')

const updatePodcastEpisodeProgressMutation = gql(`
    mutation UpdatePodcastEpisodeProgress($id: String!, $progressMilliseconds: Float!) {
        updatePodcastEpisodeProgress(id: $id, progressMilliseconds: $progressMilliseconds) {
            id
            playbackInformation {
                progressMilliseconds
            }
        }
    }
`)

export type UpdatePodcastEpisodeProgressResult = SanitizedResult<
    typeof updatePodcastEpisodeProgressMutation
>

/**
 * Persist an episode's playback offset to Firefly so the user can
 * resume where they left off on any device. Firefly is the source of
 * truth — the offset is read back off the episode's
 * playbackInformation.progressMilliseconds on the next load.
 */
export function reportPodcastEpisodeProgress(
    id: string,
    progressMilliseconds: number
): Promise<UpdatePodcastEpisodeProgressResult> {
    // Progress is a per-user, auth-only mutation — Firefly owns resume state
    // and rejects it for anonymous callers. Guard here (not just in the
    // savePodcastEpisodeProgress wrapper) so the raw mutation stays safe
    // regardless of caller.
    if (!getAuth().isSignedIn()) {
        return Promise.reject(
            new Error('reportPodcastEpisodeProgress requires an authenticated user')
        )
    }
    return gqlFetch(updatePodcastEpisodeProgressMutation, {
        id,
        progressMilliseconds,
    })
}

/**
 * Report progress to Firefly. Fire-and-forget — the caller's playback
 * flow shouldn't block on a progress save, and a network blip on save
 * shouldn't surface to the user.
 */
export function savePodcastEpisodeProgress(id: string, progressMs: number): void {
    // Progress is a per-user, auth-only mutation — Firefly owns resume state
    // and rejects it for anonymous callers. Skip the write when signed out
    // rather than firing a request that can only 403.
    if (!getAuth().isSignedIn()) return
    if (!id || !Number.isFinite(progressMs) || progressMs < 0) return
    reportPodcastEpisodeProgress(id, progressMs).catch(errorHandler)
}
